import { useNavigate } from "react-router-dom";

const WINDOW_DAYS = 180;
const M_MONTH_W = 106;
const M_TOTAL_W = M_MONTH_W * 6;
const M_PX_DAY = M_TOTAL_W / WINDOW_DAYS;

function dayDiff(a, b) {
  return Math.round((new Date(a + "T00:00:00") - new Date(b + "T00:00:00")) / 86400000);
}

function monthTicks(today) {
  const ticks = [];
  const start = new Date(today + "T00:00:00");
  const d = new Date(start.getFullYear(), start.getMonth() + 1, 1);
  while (dayDiff(d.toISOString().slice(0, 10), today) < WINDOW_DAYS) {
    const iso = d.toISOString().slice(0, 10);
    ticks.push({
      pct: (dayDiff(iso, today) / WINDOW_DAYS) * 100,
      label: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
    });
    d.setMonth(d.getMonth() + 1);
  }
  return ticks;
}

function packLanes(list, gap) {
  const lanes = [];
  for (const t of list) {
    let lane = lanes.findIndex((end) => end <= t.startOff);
    if (lane === -1) { lane = lanes.length; lanes.push(0); }
    lanes[lane] = t.endOff + gap;
    t.lane = lane;
  }
  return Math.max(1, lanes.length);
}

export default function TimelineStrip({ trips, today }) {
  const navigate = useNavigate();

  /* ---- desktop: window starts today, percent-based ---- */
  const visible = trips
    .filter((t) => t.start_date && dayDiff(t.end_date || t.start_date, today) >= 0 && dayDiff(t.start_date, today) < WINDOW_DAYS)
    .map((t) => {
      const s = Math.max(0, dayDiff(t.start_date, today));
      const e = Math.min(WINDOW_DAYS, Math.max(s + 2, dayDiff(t.end_date || t.start_date, today) + 1));
      return { ...t, left: (s / WINDOW_DAYS) * 100, width: ((e - s) / WINDOW_DAYS) * 100, startOff: s, endOff: e };
    })
    .sort((a, b) => a.startOff - b.startOff);
  const laneCount = packLanes(visible, 3);
  const ticks = monthTicks(today);

  /* ---- mobile: window starts at 1st of current month, fixed px, h-scroll ---- */
  const mStart = today.slice(0, 8) + "01";
  const todayX = dayDiff(today, mStart) * M_PX_DAY;
  const mVisible = trips
    .filter((t) => t.start_date && dayDiff(t.end_date || t.start_date, today) >= 0 && dayDiff(t.start_date, mStart) < WINDOW_DAYS)
    .map((t) => {
      const s = Math.max(0, dayDiff(t.start_date, mStart));
      const e = Math.min(WINDOW_DAYS, Math.max(s + 2, dayDiff(t.end_date || t.start_date, mStart) + 1));
      return { ...t, startOff: s, endOff: e };
    })
    .sort((a, b) => a.startOff - b.startOff);
  const mLaneCount = packLanes(mVisible, 4);
  const d0 = new Date(mStart + "T00:00:00");
  const mMonths = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(d0.getFullYear(), d0.getMonth() + i, 1);
    mMonths.push({
      x: dayDiff(d.toISOString().slice(0, 10), mStart) * M_PX_DAY,
      label: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
    });
  }

  return (
    <div className="rounded-card border border-hairline bg-surface" data-testid="timeline-strip">
      <div className="px-5 md:px-6 pt-5 pb-3 flex items-center justify-between">
        <p className="label-tag">Next 180 days</p>
        <p className="hidden md:block font-mono text-[10px] text-sand-400 tracking-wider">{visible.length} TRIP{visible.length !== 1 ? "S" : ""} IN WINDOW</p>
        {mVisible.length > 0 && (
          <span className="md:hidden font-mono text-[10px] text-dusk bg-dusk-soft rounded-full px-2.5 py-1" data-testid="timeline-swipe-pill">
            {mVisible.length} TRIP{mVisible.length !== 1 ? "S" : ""} ↔ SWIPE
          </span>
        )}
      </div>

      {/* mobile: horizontally scrollable, stacked lanes */}
      <div className="md:hidden px-5 pb-5" data-testid="timeline-mobile">
        {mVisible.length === 0 ? (
          <p className="py-6 text-center text-xs text-sand-400 font-mono tracking-wider">NO TRIPS IN THE NEXT 180 DAYS</p>
        ) : (
          <div className="noscrollbar overflow-x-auto rounded-2xl border border-hairline bg-paper/40">
            <div className="relative py-3" style={{ width: `${M_TOTAL_W + 32}px` }}>
              <div className="relative mx-4">
                {/* month lane */}
                <div className="relative h-5 border-b border-sand-200">
                  {mMonths.map((m, i) => (
                    <span
                      key={m.label + i}
                      className={`absolute top-0 font-mono text-[10px] tracking-wider ${i === 0 ? "text-ink" : "text-sand-400"}`}
                      style={{ left: `${m.x}px` }}
                    >
                      {m.label}
                    </span>
                  ))}
                </div>
                {/* trip lanes */}
                <div className="relative mt-3" style={{ height: `${mLaneCount * 28 - 6}px` }}>
                  {mVisible.map((t) => {
                    const allPlanned = t.planned > 0 && t.confirmed === 0;
                    const labelW = Math.min(t.name.length * 6.5 + 18, 110);
                    const w = Math.max((t.endOff - t.startOff) * M_PX_DAY, labelW);
                    const l = Math.min(t.startOff * M_PX_DAY, M_TOTAL_W - w);
                    return (
                      <button
                        key={t.id}
                        onClick={() => navigate(`/trips/${t.id}`)}
                        className={`absolute h-[22px] rounded-[7px] flex items-center px-2 overflow-hidden whitespace-nowrap active:scale-95 transition-transform ${
                          allPlanned ? "bg-[#F3E7D3] border border-[#E9D5AE]" : "bg-ember"
                        }`}
                        style={{ left: `${l}px`, width: `${w}px`, top: `${t.lane * 28}px` }}
                        data-testid={`timeline-bar-m-${t.id}`}
                      >
                        <span className={`font-mono text-[10px] font-medium truncate ${allPlanned ? "text-hour-deep" : "text-cream"}`}>
                          {t.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {/* today marker */}
                <div className="absolute w-[2px] bg-ember" style={{ left: `${todayX}px`, top: "24px", bottom: "0px" }} />
                <span
                  className="absolute -translate-x-1/2 font-mono text-[8px] font-semibold text-ember"
                  style={{ left: `${todayX}px`, top: "14px" }}
                >
                  TODAY
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* desktop: fit-to-width */}
      <div className="hidden md:block px-6 pb-6">
        <div className="relative" style={{ height: `${28 + laneCount * 34}px` }}>
          {/* month ticks */}
          {ticks.map((tk) => (
            <div key={tk.label + tk.pct} className="absolute top-0 bottom-0 border-l border-sand-100" style={{ left: `${tk.pct}%` }}>
              <span className="absolute -top-0.5 left-1.5 font-mono text-[9px] text-sand-400 tracking-widest">{tk.label}</span>
            </div>
          ))}
          {/* today marker */}
          <div className="absolute top-0 bottom-0 border-l-2 border-sand-950" style={{ left: 0 }}>
            <span className="absolute -top-0.5 left-1.5 font-mono text-[9px] font-semibold text-sand-950 tracking-widest">TODAY</span>
          </div>
          {/* trip bars */}
          {visible.map((t) => {
            const allPlanned = t.planned > 0 && t.confirmed === 0;
            const narrow = t.width < 10;
            return (
              <div key={t.id}>
                <button
                  onClick={() => navigate(`/trips/${t.id}`)}
                  className="absolute h-6 flex items-center px-2 overflow-hidden hover:-translate-y-[2px] transition-transform duration-200 ease-out cursor-pointer"
                  style={{
                    left: `${t.left}%`,
                    width: `${Math.max(t.width, 3)}%`,
                    top: `${26 + t.lane * 34}px`,
                    background: allPlanned ? "#F3E7D3" : "#C15A38",
                    border: allPlanned ? "1px solid #E9D5AE" : "none",
                  }}
                  title={`${t.name} · ${t.start_date}${t.end_date ? " → " + t.end_date : ""}`}
                  data-testid={`timeline-bar-${t.id}`}
                >
                  {!narrow && (
                    <span className={`font-mono text-[10px] font-medium truncate ${allPlanned ? "text-[#B07B2E]" : "text-white"}`}>
                      {t.name}
                    </span>
                  )}
                </button>
                {narrow && (
                  <span
                    className="absolute font-mono text-[10px] font-medium text-sand-700 whitespace-nowrap pointer-events-none"
                    style={{ left: `calc(${t.left + Math.max(t.width, 3)}% + 6px)`, top: `${31 + t.lane * 34}px` }}
                  >
                    {t.name}
                  </span>
                )}
              </div>
            );
          })}
          {visible.length === 0 && (
            <p className="absolute inset-0 flex items-center justify-center text-xs text-sand-400 font-mono tracking-wider">
              NO TRIPS IN THE NEXT 180 DAYS
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
